<template>
    <div
        class="flex h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200"
    >
        <!-- Mobile sidebar backdrop -->
        <div
            v-if="sidebarOpen && !isExamMode"
            class="fixed inset-0 z-40 md:hidden"
            @click="closeSidebar"
        >
            <div
                class="absolute inset-0 bg-gray-600 dark:bg-gray-800 opacity-75"
            ></div>
        </div>

        <!-- Sidebar -->
        <aside
            v-if="!isExamMode"
            :class="[
                'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:inset-0 md:flex-shrink-0',
                sidebarOpen
                    ? 'translate-x-0'
                    : '-translate-x-full md:translate-x-0',
            ]"
        >
            <!-- Sidebar header -->
            <div
                class="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700"
            >
                <div class="flex items-center">
                    <div class="flex-shrink-0">
                        <div
                            class="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center"
                        >
                            <span class="text-white font-bold text-lg"
                                >LMS</span
                            >
                        </div>
                    </div>
                </div>
                <button
                    @click="closeSidebar"
                    class="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                >
                    <XMarkIcon class="h-6 w-6" />
                </button>
            </div>

            <!-- Navigation -->
            <nav class="mt-5 px-2">
                <div class="space-y-1">
                    <template
                        v-for="item in filteredNavigation"
                        :key="item.nameKey"
                    >
                        <router-link
                            v-if="!item.children"
                            :to="item.href"
                            :class="[
                                'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150',
                                $route.path === item.href
                                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border-r-2 border-primary-500'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white',
                            ]"
                        >
                            <component
                                :is="item.icon"
                                :class="[
                                    'mr-2 h-5 w-5 flex-shrink-0',
                                    $route.path === item.href
                                        ? 'text-primary-500'
                                        : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300',
                                ]"
                            />
                            {{ t(item.nameKey) }}
                        </router-link>

                        <!-- Nested navigation -->
                        <div v-else class="space-y-1">
                            <button
                                @click.stop="toggleSubmenu(item.nameKey)"
                                :class="[
                                    'w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150',
                                    isSubmenuOpen(item.nameKey) ||
                                    isParentOfActiveRoute(item.nameKey)
                                        ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white',
                                ]"
                            >
                                <component
                                    :is="item.icon"
                                    class="mr-2 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300"
                                />
                                {{ t(item.nameKey) }}
                                <ChevronDownIcon
                                    :class="[
                                        'ml-auto h-4 w-4 transition-transform duration-150',
                                        isSubmenuOpen(item.nameKey)
                                            ? 'rotate-180'
                                            : '',
                                    ]"
                                />
                            </button>

                            <div
                                v-show="isSubmenuOpen(item.nameKey)"
                                class="mt-1 space-y-1 pl-6"
                            >
                                <router-link
                                    v-for="child in item.children"
                                    :key="child.nameKey"
                                    :to="child.href"
                                    @click.stop="handleChildNavigation"
                                    :class="[
                                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150',
                                        $route.path === child.href
                                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border-r-2 border-primary-500'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700',
                                    ]"
                                >
                                    <component
                                        :is="child.icon"
                                        :class="[
                                            'mr-2 h-4 w-4 flex-shrink-0',
                                            $route.path === child.href
                                                ? 'text-primary-500'
                                                : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300',
                                        ]"
                                    />
                                    {{ t(child.nameKey) }}
                                </router-link>
                            </div>
                        </div>
                    </template>
                </div>
            </nav>
        </aside>

        <!-- Main content area -->
        <div class="flex flex-col flex-1 min-h-screen overflow-hidden">
            <!-- Top header -->
            <header
                v-if="!isExamMode"
                class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 flex-shrink-0"
            >
                <div
                    class="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8"
                >
                    <!-- Mobile menu button -->
                    <button
                        @click="openSidebar"
                        class="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                    >
                        <Bars3Icon class="h-6 w-6" />
                    </button>

                    <!-- Search bar -->
                    <div class="flex-1 max-w-lg mx-4">
                        <div class="relative">
                            <div
                                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                            >
                                <MagnifyingGlassIcon
                                    class="h-5 w-5 text-gray-400"
                                />
                            </div>
                            <input
                                type="text"
                                :placeholder="t('common.search')"
                                class="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                        </div>
                    </div>

                    <!-- Right side items -->
                    <div class="flex items-center space-x-4">
                        <!-- Theme toggle -->
                        <button
                            @click="themeStore.toggleTheme()"
                            class="p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
                            :title="`Current theme: ${themeStore.theme}`"
                        >
                            <SunIcon v-if="themeStore.isDark" class="h-5 w-5" />
                            <MoonIcon v-else class="h-5 w-5" />
                        </button>

                        <!-- Language switch -->
                        <button
                            @click="languageStore.toggleLanguage()"
                            class="flex items-center space-x-1 px-3 py-1.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
                            :title="`${t('language.current')}: ${t(languageStore.isEnglish ? 'language.english' : 'language.indonesian')}`"
                        >
                            <LanguageIcon class="h-4 w-4" />
                            <span>{{ languageStore.languageName }}</span>
                        </button>

                        <!-- Notifications -->
                        <button
                            class="p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
                        >
                            <BellIcon class="h-5 w-5" />
                        </button>

                        <!-- User menu -->
                        <div class="relative" ref="dropdownWrapper">
                            <button
                                @click.stop="toggleUserMenu"
                                class="flex items-center space-x-3 p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
                            >
                                <div
                                    class="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center"
                                >
                                    <span
                                        class="text-white text-sm font-medium"
                                    >
                                        {{ userInitials }}
                                    </span>
                                </div>
                                <ChevronDownIcon class="h-4 w-4" />
                            </button>

                            <!-- User dropdown -->
                            <div
                                v-if="userMenuOpen"
                                class="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50"
                            >
                                <div class="py-1">
                                    <router-link
                                        to="/profile"
                                        class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        {{ t("navigation.profile") }}
                                    </router-link>
                                    <router-link
                                        to="/settings"
                                        class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        {{ t("navigation.settings") }}
                                    </router-link>
                                    <button
                                        @click="logout"
                                        class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        {{ t("auth.logout") }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <!-- Page content -->
            <main class="flex-1 overflow-y-auto">
                <div class="pl-2 pr-4 py-4">
                    <div class="max-w-8xl mx-auto">
                        <!-- Page header -->
                        <div v-if="$slots.header" class="mb-4">
                            <slot name="header" />
                        </div>

                        <!-- Main content slot -->
                        <router-view />
                    </div>
                </div>
            </main>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useThemeStore } from "@/stores/themeStore";
import { useAuthStore } from "@/stores/authStore";
import { useUserStore } from "@/stores/userStore";
import { useLanguageStore } from "@/stores/languageStore";
import { usePresenceChannel } from "@/composables/usePresenceChannel";
import { useI18n } from "vue-i18n";
import {
    Bars3Icon,
    XMarkIcon,
    ChevronDownIcon,
    MagnifyingGlassIcon,
    SunIcon,
    MoonIcon,
    BellIcon,
    HomeIcon,
    BookOpenIcon,
    ClipboardDocumentListIcon,
    QuestionMarkCircleIcon,
    UserGroupIcon,
    UserIcon,
    ChartBarIcon,
    Cog6ToothIcon,
    DocumentTextIcon,
    AcademicCapIcon,
    CalendarIcon,
    UsersIcon,
    ShieldCheckIcon,
    LanguageIcon,
} from "@heroicons/vue/24/outline";

// Stores
const themeStore = useThemeStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const languageStore = useLanguageStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Presence channel for online users tracking
const { connect: connectPresence, disconnect: disconnectPresence } =
    usePresenceChannel();

// State
const sidebarOpen = ref(false);
const userMenuOpen = ref(false);
const logoLoaded = ref(true);
const openSubmenus = ref<string[]>([]);
const dropdownWrapper = ref<HTMLElement | null>(null);

// Navigation items with translation keys
const navigation = [
    {
        nameKey: "navigation.dashboard",
        href: "/dashboard",
        icon: HomeIcon,
    },
    {
        nameKey: "navigation.masterData",
        href: "#",
        icon: Cog6ToothIcon,
        children: [
            {
                nameKey: "navigation.users",
                href: "/users",
                icon: UserIcon,
            },
            {
                nameKey: "navigation.roles",
                href: "/roles",
                icon: UsersIcon,
            },
            {
                nameKey: "navigation.permissions",
                href: "/permissions",
                icon: ShieldCheckIcon,
            },
            {
                nameKey: "navigation.academicYears",
                href: "/academic-years",
                icon: CalendarIcon,
            },
            {
                nameKey: "navigation.majors",
                href: "/majors",
                icon: AcademicCapIcon,
            },
            {
                nameKey: "navigation.semesters",
                href: "/semesters",
                icon: CalendarIcon,
            },
            {
                nameKey: "navigation.teachers",
                href: "/teachers",
                icon: UserGroupIcon,
            },
            {
                nameKey: "navigation.classes",
                href: "/classes",
                icon: AcademicCapIcon,
            },
            {
                nameKey: "navigation.subjects",
                href: "/subjects",
                icon: BookOpenIcon,
            },
            {
                nameKey: "navigation.classSubjects",
                href: "/class-subjects",
                icon: AcademicCapIcon,
            },
            {
                nameKey: "navigation.students",
                href: "/students",
                icon: UserIcon,
            },
            {
                nameKey: "navigation.studentClasses",
                href: "/student-classes",
                icon: AcademicCapIcon,
            },
        ],
    },
    {
        nameKey: "navigation.learning",
        href: "#",
        icon: BookOpenIcon,
        children: [
            {
                nameKey: "navigation.materials",
                href: "/materials",
                icon: DocumentTextIcon,
            },
            {
                nameKey: "navigation.assignments",
                href: "/assignments",
                icon: ClipboardDocumentListIcon,
            },
            {
                nameKey: "navigation.questionBank",
                href: "/question-banks",
                icon: QuestionMarkCircleIcon,
            },
            {
                nameKey: "navigation.exams",
                href: "/exams",
                icon: QuestionMarkCircleIcon,
            },
        ],
    },
    {
        nameKey: "navigation.studentMaterials",
        href: "/student-materials",
        icon: DocumentTextIcon,
    },
    {
        nameKey: "navigation.myAssignments",
        href: "/student-assignments",
        icon: ClipboardDocumentListIcon,
    },
    {
        nameKey: "navigation.myExams",
        href: "/student-exams",
        icon: QuestionMarkCircleIcon,
    },
    {
        nameKey: "navigation.attendance",
        href: "/attendance",
        icon: CalendarIcon,
    },
    {
        nameKey: "navigation.reports",
        href: "/reports",
        icon: ChartBarIcon,
    },
    {
        nameKey: "navigation.forums",
        href: "/forums",
        icon: UserGroupIcon,
    },
    {
        nameKey: "navigation.settings",
        href: "/settings",
        icon: Cog6ToothIcon,
    },
];

// Computed
const userInitials = computed(() => {
    // Null-safe check for user and name
    if (!authStore.user || !authStore.user.name) {
        return "U";
    }

    // Trim whitespace and check if name is empty after trimming
    const trimmedName = authStore.user.name.trim();
    if (!trimmedName) {
        return "U";
    }

    const names = trimmedName.split(/\s+/);
    if (names.length >= 2) {
        // Safely access first and last name characters
        const firstInitial = names[0]?.[0] || "";
        const lastInitial = names[names.length - 1]?.[0] || "";
        return (firstInitial + lastInitial).toUpperCase();
    } else {
        // Single name - take first 2 characters safely
        return trimmedName.substring(0, 2).toUpperCase();
    }
});

// Exam mode detection - hide sidebar and navigation when student is actively taking an exam
const isExamMode = computed(() => {
    // Check if current route is an exam-taking page
    const examRoutePatterns = [
        /^\/student-exams\/[^\/]+\/attempt\/[^\/]+$/, // /student-exams/{examId}/attempt/{attemptId}
    ];

    return examRoutePatterns.some((pattern) => pattern.test(route.path));
});

// Permission-based navigation filtering
const filteredNavigation = computed(() => {
    return navigation.filter((item) => {
        // Ensure item has valid icon before processing
        if (!item || !item.icon) {
            return false;
        }

        // ========== STUDENT ROLE ==========
        if (authStore.hasRole("student")) {
            // Students see: Dashboard, Materials, My Assignments, and My Exams
            if (item.nameKey === "navigation.dashboard") {
                return true;
            }
            if (item.href === "/student-materials") {
                return true;
            }
            if (item.href === "/student-assignments") {
                return true;
            }
            if (item.href === "/student-exams") {
                return true;
            }
            // Hide everything else for students
            return false;
        }

        // ========== TEACHER ROLE ==========
        if (authStore.hasRole("teacher")) {
            // Teachers see: Dashboard, Materials, Question Bank, Exams
            if (item.nameKey === "navigation.dashboard") {
                return true;
            }
            if (item.nameKey === "navigation.learning") {
                // Filter Learning children for teachers
                const filteredChildren =
                    item.children?.filter((child) => {
                        // Teachers see: Materials, Assignments, Question Bank, Exams
                        if (child.href === "/materials") {
                            return true;
                        }
                        if (child.href === "/assignments") {
                            return true;
                        }
                        if (child.href === "/question-banks") {
                            return true;
                        }
                        if (child.href === "/exams") {
                            return true;
                        }
                        return false;
                    }) || [];
                // Only show Learning menu if it has visible children
                return filteredChildren.length > 0;
            }
            // Hide everything else for teachers (Master Data, Attendance, Reports, Forums, Settings)
            return false;
        }

        // ========== ADMIN / SUPER-ADMIN ROLE ==========
        if (
            authStore.hasRole("administrator") ||
            authStore.hasRole("super-administrator")
        ) {
            // Admins and super-admins see all navigation items EXCEPT My Exams, Student Materials, and My Assignments
            // My Exams, Student Materials, and My Assignments are only for students
            if (item.href === "/student-exams") {
                return false;
            }
            if (item.href === "/student-materials") {
                return false;
            }
            if (item.href === "/student-assignments") {
                return false;
            }
            // No permission filtering needed for other items
            return true;
        }

        // ========== DEFAULT BEHAVIOR (for other roles/fallback) ==========
        // If item has no children, check if user has permission or role
        if (!item.children) {
            // Special handling for student-only routes
            if (item.href === "/student-exams") {
                return authStore.hasRole("student");
            }
            if (item.href === "/student-assignments") {
                return authStore.hasRole("student");
            }

            // Dashboard and other basic items are always visible
            return true;
        }

        // For items with children, filter children based on permissions
        const filteredChildren = item.children.filter((child) => {
            // Ensure child has valid icon before processing
            if (!child || !child.icon) {
                console.warn(
                    "DEBUG: Navigation child item missing icon:",
                    child,
                );
                return false;
            }

            // Map routes to permissions
            const permissionMap: { [key: string]: string } = {
                "/users": "users.view",
                "/roles": "roles.view",
                "/permissions": "permissions.view",
                "/academic-years": "academic-years.view",
                "/majors": "majors.view",
                "/semesters": "semesters.view",
                "/teachers": "teachers.view",
                "/classes": "classes.view",
                "/subjects": "subjects.view",
                "/class-subjects": "class-subjects.view",
                "/students": "students.view",
                "/materials": "materials.view",
                "/assignments": "assignments.view",
                "/question-banks": "question-bank.view",
                "/exams": "exams.view",
                "/attendance": "attendance.view",
                "/reports": "reports.view",
                "/forums": "forums.view",
            };

            const requiredPermission = permissionMap[child.href];
            if (!requiredPermission) {
                return true; // Show items without specific permission requirements
            }

            const hasPermission = authStore.hasPermission(requiredPermission);
            return hasPermission;
        });

        // Only show parent item if it has visible children
        return filteredChildren.length > 0;
    });
});

// Methods
const openSidebar = () => {
    sidebarOpen.value = true;
};

const closeSidebar = () => {
    sidebarOpen.value = false;
};

const toggleUserMenu = () => {
    userMenuOpen.value = !userMenuOpen.value;

    // Add/remove click outside listener based on dropdown state
    if (userMenuOpen.value) {
        document.addEventListener("click", handleClickOutside);
    } else {
        document.removeEventListener("click", handleClickOutside);
    }
};

const closeUserMenu = () => {
    userMenuOpen.value = false;
    // Remove click outside listener when dropdown is closed
    document.removeEventListener("click", handleClickOutside);
};

const toggleSubmenu = (name: string) => {
    const index = openSubmenus.value.indexOf(name);
    if (index > -1) {
        openSubmenus.value.splice(index, 1);
    } else {
        openSubmenus.value.push(name);
    }
};

const isSubmenuOpen = (name: string) => {
    return openSubmenus.value.includes(name);
};

const isParentOfActiveRoute = (parentKey: string) => {
    const parentItem = navigation.find((item) => item.nameKey === parentKey);
    if (!parentItem || !parentItem.children) return false;

    return parentItem.children.some(
        (child) => router.currentRoute.value.path === child.href,
    );
};

const handleChildNavigation = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 768) {
        closeSidebar();
    }
};

const logout = async () => {
    try {
        await authStore.logout();
        router.push("/login");
    } catch (error) {
        console.error("Logout failed:", error);
        alert("Failed to logout, please try again.");
    }
};

// Lifecycle
onMounted(() => {
    // Initialize theme
    themeStore.initializeTheme();

    // Connect to presence channel for online users tracking
    if (authStore.isAuthenticated) {
        connectPresence();
    }

    // Auto-open submenu based on current route
    const currentPath = router.currentRoute.value.path;
    navigation.forEach((item) => {
        if (
            item.children &&
            item.children.some((child) => child.href === currentPath)
        ) {
            if (!openSubmenus.value.includes(item.nameKey)) {
                openSubmenus.value.push(item.nameKey);
            }
        }
    });

    // Add Escape key listener
    const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === "Escape" && userMenuOpen.value) {
            closeUserMenu();
        }
    };

    document.addEventListener("keydown", handleEscapeKey);

    // Store for cleanup
    (window as any)._escapeKeyHandler = handleEscapeKey;
});

// Simple click outside handler using the wrapper ref
const handleClickOutside = (event: MouseEvent) => {
    if (
        dropdownWrapper.value &&
        !dropdownWrapper.value.contains(event.target as Node)
    ) {
        closeUserMenu();
    }
};

onUnmounted(() => {
    // Cleanup
    document.removeEventListener("click", handleClickOutside);

    // Disconnect presence channel
    disconnectPresence();

    // Cleanup Escape key listener
    if ((window as any)._escapeKeyHandler) {
        document.removeEventListener(
            "keydown",
            (window as any)._escapeKeyHandler,
        );
        delete (window as any)._escapeKeyHandler;
    }
});
</script>

<style scoped>
/* Custom scrollbar for sidebar */
aside {
    scrollbar-width: thin;
    scrollbar-color: rgb(156 163 175) transparent;
}

aside::-webkit-scrollbar {
    width: 6px;
}

aside::-webkit-scrollbar-track {
    background: transparent;
}

aside::-webkit-scrollbar-thumb {
    background-color: rgb(156 163 175);
    border-radius: 3px;
}

aside::-webkit-scrollbar-thumb:hover {
    background-color: rgb(107 114 128);
}

/* Dark mode scrollbar */
.dark aside {
    scrollbar-color: rgb(75 85 99) transparent;
}

.dark aside::-webkit-scrollbar-thumb {
    background-color: rgb(75 85 99);
}

.dark aside::-webkit-scrollbar-thumb:hover {
    background-color: rgb(107 114 128);
}
</style>
